'use client'

import Image from 'next/image'
import Link from 'next/link'
import { Dispatch, SetStateAction, useRef, useEffect, useCallback } from 'react'

import { ISearchResponse } from '@/_types/search'

import { AMobileSearchResults } from './a-mobile-search-results'

interface AMobileHeaderBottomProps {
	searchOpen: boolean
	setSearchOpen: Dispatch<SetStateAction<boolean>>
	searchQuery: string
	onSearchQueryChange: (query: string) => void
	onSearchClose: () => void
	searchData: ISearchResponse | undefined
	isSearchLoading: boolean
	isSearching: boolean
	hasSearchTerm: boolean
	onMenuToggle: () => void
	onLinkClick: () => void
	menuOpen?: boolean
}

export const AMobileHeaderBottom = ({
	searchOpen,
	setSearchOpen,
	searchQuery,
	onSearchQueryChange,
	onSearchClose,
	searchData,
	isSearchLoading,
	isSearching,
	hasSearchTerm,
	onMenuToggle,
	onLinkClick,
	menuOpen = false
}: AMobileHeaderBottomProps) => {
	const searchInputRef = useRef<HTMLInputElement>(null)
	const searchContainerRef = useRef<HTMLDivElement>(null)
	const searchButtonRef = useRef<HTMLButtonElement>(null)

	// Auto-focus input when search opens
	useEffect(() => {
		if (searchOpen && searchInputRef.current) {
			// Small delay to ensure animation starts before focus
			const timer = setTimeout(() => {
				searchInputRef.current?.focus()
			}, 100)
			return () => clearTimeout(timer)
		}
	}, [searchOpen])

	// Close search on Escape key
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape' && searchOpen) {
				onSearchClose()
			}
		}

		document.addEventListener('keydown', handleEscape)
		return () => document.removeEventListener('keydown', handleEscape)
	}, [searchOpen, onSearchClose])

	// Close search when clicking outside (but not on the search button)
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			const target = e.target as HTMLElement

			// CRITICAL: Ignore clicks on desktop header elements
			// Mobile header is hidden on desktop but its event listeners still run!
			if (target.closest?.('.desktop-header')) {
				return
			}

			if (
				searchOpen &&
				searchContainerRef.current &&
				searchButtonRef.current &&
				!searchContainerRef.current.contains(target) &&
				!searchButtonRef.current.contains(target)
			) {
				onSearchClose()
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [searchOpen, onSearchClose])

	const handleSearchToggle = useCallback(() => {
		if (searchOpen) {
			onSearchClose()
		} else {
			setSearchOpen(true)
		}
		// Remove focus from button after click to prevent stuck hover/focus state on mobile
		if (searchButtonRef.current) {
			searchButtonRef.current.blur()
		}
	}, [searchOpen, setSearchOpen, onSearchClose])

	const handleSearchSubmit = useCallback((e: React.FormEvent) => {
		e.preventDefault()
		// Form submission is handled by the search results links
	}, [])

	const handleSearchInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onSearchQueryChange(e.target.value)
		},
		[onSearchQueryChange]
	)

	const handleResultClick = useCallback(() => {
		onSearchClose()
		onLinkClick()
	}, [onSearchClose, onLinkClick])

	return (
		<div
			className={`header-mobile__bottom show_lgm ${
				menuOpen ? 'header-mobile__bottom--menu-open' : ''
			} ${searchOpen ? 'header-mobile__bottom--search-active' : ''}`}
		>
			<div className='header-mobile__search-container' ref={searchContainerRef}>
				<div
					className={`header-mobile__logo-wrapper ${
						searchOpen ? 'header-mobile__logo-wrapper--hidden' : ''
					}`}
				>
					<Link
						href='/'
						title='Domov'
						className='header-mobile__logo'
						onClick={onLinkClick}
						prefetch={true}
					>
						<Image
							unoptimized={true}
							src='/assets/images/medante-logo-dark.svg'
							alt='Medante'
							width={431}
							height={115}
							priority
							loading='eager'
						/>
					</Link>
				</div>

				<form
					className={`header-mobile__search-input-wrapper ${
						searchOpen ? 'header-mobile__search-input-wrapper--open' : ''
					}`}
					onSubmit={handleSearchSubmit}
				>
					<input
						ref={searchInputRef}
						type='text'
						placeholder='Hľadať...'
						className='header-mobile__search-input'
						aria-label='Vyhľadávanie'
						onChange={handleSearchInputChange}
						value={searchQuery}
					/>
				</form>

				{searchOpen && (
					<AMobileSearchResults
						results={searchData?.data || []}
						isLoading={isSearchLoading}
						isSearching={isSearching}
						hasSearchTerm={hasSearchTerm}
						onResultClick={handleResultClick}
						searchQuery={searchQuery}
					/>
				)}
			</div>

			<div className='header-mobile__actions'>
				<button
					ref={searchButtonRef}
					type='button'
					className={`header-mobile__search-button ${
						searchOpen ? 'header-mobile__search-button--active' : ''
					}`}
					onClick={handleSearchToggle}
					aria-label={searchOpen ? 'Zatvoriť vyhľadávanie' : 'Otvoriť vyhľadávanie'}
					aria-expanded={searchOpen}
				>
					<svg className='sicofill header-mobile__search-button-icon'>
						<use href='/assets/layout/icons.svg#s_mag' />
					</svg>
				</button>

				<button
					className='header-mobile__menu-button cursor-pointer'
					onClick={onMenuToggle}
					name='menu'
					title='Menu'
					aria-label='Menu'
					aria-expanded={menuOpen}
				>
					<svg className='sicofill'>
						<use href='/assets/layout/icons.svg#s_menu' />
					</svg>
				</button>
			</div>
		</div>
	)
}
